import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SummaryV3AlarmTrendChart from "./SummaryV3AlarmTrendChart";
import { translateText } from "../../../../shared/utils/historyTranslation";
import {
  fmtNum,
  getAlarmTrendPoints,
  getCageShortTag,
  getMapCenterTone,
  getSimpleCageTone,
  SUMMARY_V3_MAP_TOOLTIP_EVENT,
} from "../utils/summaryDashboardV3Utils";

export default React.memo(function SummaryV3CageMap({
  summaries,
  canNavigateCenters,
  enteringCenterKeys,
  windowAnimating,
  windowShiftDirection,
  hideNormalCenters,
  onPreviousCenters,
  onNextCenters,
  onToggleNormalCenters,
  canSeeSensorFailures,
  ignoredCenterKeys = [],
  onToggleIgnoredCenter,
  translate,
}) {
  const t = useCallback((key, fallback) => translateText(translate, key, fallback), [translate]);
  const ignoredCenterSet = useMemo(() => new Set(ignoredCenterKeys), [ignoredCenterKeys]);

  return (
    <section className="summary-v3__mapPanel">
      <div className="summary-v3__panelHead">
        <h3>{t("summary_v3_quick_cage_map", "Mapa rapido de jaulas")}</h3>
        <div className="summary-v3__legend">
          <div className="summary-v3__windowControls">
            <button
              type="button"
              className="summary-v3__windowButton"
              onClick={onPreviousCenters}
              disabled={!canNavigateCenters}
              title={t("summary_v3_show_previous_centers", "Mostrar centros anteriores")}
            >
              {"<"}
            </button>
            <button
              type="button"
              className="summary-v3__windowButton"
              onClick={onNextCenters}
              disabled={!canNavigateCenters}
              title={t("summary_v3_show_next_centers", "Mostrar centros siguientes")}
            >
              {">"}
            </button>
          </div>
          <button
            type="button"
            className={`summary-v3__mapToggle ${hideNormalCenters ? "is-active" : ""}`}
            onClick={onToggleNormalCenters}
          >
            {hideNormalCenters
              ? t("summary_v3_show_normal", "Mostrar normales")
              : t("summary_v3_hide_normal", "Ocultar normales")}
          </button>
          <span><i className="summary-v3__legendDot summary-v3__legendDot--normal" />{t("summary_v3_legend_normal", "Normal")}</span>
          <span><i className="summary-v3__legendDot summary-v3__legendDot--injecting" />{t("summary_v3_legend_injecting", "Inyectando")}</span>
          <span><i className="summary-v3__legendDot summary-v3__legendDot--manual" />{t("summary_v3_legend_manual", "Manual")}</span>
          <span><i className="summary-v3__legendDot summary-v3__legendDot--warning" />{t("summary_v3_legend_no_injection", "Sin Inyeccion")}</span>
          <span><i className="summary-v3__legendDot summary-v3__legendDot--problem" />{t("summary_v3_legend_problems", "Problemas")}</span>
        </div>
      </div>
      <div className="summary-v3__mapRows">
        {summaries.map((summary, index) => (
          <SummaryV3MapRow
            key={summary.centerKey}
            summary={summary}
            index={index}
            entering={enteringCenterKeys?.has(summary.centerKey)}
            shifting={windowAnimating && !enteringCenterKeys?.has(summary.centerKey)}
            shiftDirection={windowShiftDirection}
            canSeeSensorFailures={canSeeSensorFailures}
            ignored={ignoredCenterSet.has(summary.centerKey)}
            onToggleIgnored={onToggleIgnoredCenter}
            translate={translate}
          />
        ))}
        {!summaries.length ? (
          <div className="summary-v3__empty">
            {t("summary_v3_no_problem_centers", "No hay centros con problemas para mostrar.")}
          </div>
        ) : null}
      </div>
    </section>
  );
});

const SummaryV3MapRow = React.memo(function SummaryV3MapRow({
  summary,
  index,
  entering,
  shifting,
  shiftDirection,
  canSeeSensorFailures,
  ignored,
  onToggleIgnored,
  translate,
}) {
  const tone = useMemo(() => (ignored ? "normal" : getMapCenterTone(summary)), [summary, ignored]);
  const rowStyle = useMemo(() => ({ "--row-index": index }), [index]);
  const handleToggleIgnored = useCallback(() => onToggleIgnored?.(summary.centerKey), [onToggleIgnored, summary.centerKey]);
  const generatorItems = summary.generators?.items || [];
  const t = useCallback((key, fallback) => translateText(translate, key, fallback), [translate]);

  return (
    <div
      className={`summary-v3__mapRow summary-v3__mapRow--${tone} ${ignored ? "is-ignored" : ""} ${entering ? "is-entering" : ""} ${
        shifting ? `is-shifting-${shiftDirection}` : ""
      }`}
      style={rowStyle}
    >
      <div className="summary-v3__mapCenter">
        <strong>{summary.centerName}</strong>
        <button
          type="button"
          className={`summary-v3__ignoreCenterButton ${ignored ? "is-active" : ""}`}
          onClick={handleToggleIgnored}
          title={
            ignored
              ? t("summary_v3_restore_center_map", "Restaurar centro en el mapa")
              : t("summary_v3_ignore_center_map", "Ignorar centro en el mapa")
          }
        >
          {ignored ? t("summary_v3_restore", "Restaurar") : t("summary_v3_ignore", "Ignorar")}
        </button>
      </div>
      <div className="summary-v3__cages">
        {summary.cages.map((cage) => (
          <SummaryV3CageDot
            key={cage.selectionKey}
            centerName={summary.centerName}
            cage={cage}
            canSeeSensorFailures={canSeeSensorFailures}
            ignored={ignored}
            translate={translate}
          />
        ))}
      </div>
      <div className="summary-v3__mapGenerators">
        {generatorItems.length ? (
          generatorItems.map((generator, generatorIndex) => (
            <SummaryV3GeneratorIcon
              key={`${summary.centerKey}-${generator.name}-${generatorIndex}`}
              generator={generator}
              index={generatorIndex}
              ignored={ignored}
              translate={translate}
            />
          ))
        ) : (
          <span className="summary-v3__generatorEmpty">-</span>
        )}
      </div>
    </div>
  );
});

const GENERATOR_TONE_COLORS = {
  ok: "#296f4d",
  danger: "#7f1d1d",
  warning: "#d7c81d",
  muted: "#747474",
};

const SummaryV3GeneratorIcon = React.memo(function SummaryV3GeneratorIcon({ generator, index, ignored, translate }) {
  const color = ignored ? GENERATOR_TONE_COLORS.muted : GENERATOR_TONE_COLORS[generator.tone] || GENERATOR_TONE_COLORS.muted;
  const label = ignored ? translateText(translate, "summary_v3_ignored_center", "Centro ignorado") : generator.statusLabel;

  return (
    <span className="summary-v3__generatorIconHost" title={`${translateText(translate, "generator", "Generador")} ${generator.name || index + 1}: ${label}`}>
      <svg viewBox="0 0 220 140" className="summary-v3__generatorIcon" aria-hidden="true">
        <polygon points="100,65 160,65 130,15" fill="#0c1c2d" />
        <g
          stroke={color}
          fill="none"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          transform="translate(30, -8)"
        >
          <rect x="20" y="35" width="120" height="70" rx="8" ry="8" />
          <rect x="10" y="50" width="8" height="40" rx="4" ry="4" />
          <rect x="40" y="50" width="35" height="4" />
          <line x1="40" y1="65" x2="70" y2="65" />
          <line x1="40" y1="75" x2="70" y2="75" />
          <line x1="40" y1="85" x2="70" y2="85" />
          <circle cx="90" cy="52" r="3" />
          <line x1="20" y1="125" x2="150" y2="125" />
          <rect x="40" y="105" width="14" height="14" rx="4" ry="4" />
          <rect x="110" y="105" width="14" height="14" rx="4" ry="4" />
          <polygon points="100,65 160,65 130,15" />
          <path d="M131 32 L125 45 L133 45 L127 57" />
        </g>
      </svg>
    </span>
  );
});

const SummaryV3CageDot = React.memo(function SummaryV3CageDot({ centerName, cage, canSeeSensorFailures, ignored, translate }) {
  const hostRef = useRef(null);
  const closeTimerRef = useRef(null);
  const tooltipPinnedRef = useRef(false);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [tooltipPinned, setTooltipPinned] = useState(false);
  const alarmTrend = useMemo(
    () => (canSeeSensorFailures && !ignored ? getAlarmTrendPoints(cage, translate) : []),
    [canSeeSensorFailures, cage, ignored, translate]
  );
  const rows = useMemo(() => {
    const sensorRows = canSeeSensorFailures && cage.sensorProblems.length
      ? cage.sensorProblems.map((problem) => [problem.label, problem.detail])
      : [];

    return [
      ...(ignored
        ? [[translateText(translate, "centro", "Centro"), translateText(translate, "summary_v3_ignored_quick_map", "Ignorado en mapa rapido")]]
        : []),
      ["O2", fmtNum(cage.o2)],
      [translateText(translate, "temperatura", "Temperatura"), fmtNum(cage.temperature)],
      [translateText(translate, "salinidad", "Salinidad"), fmtNum(cage.salinity)],
      [
        translateText(translate, "status", "Estado"),
        ignored ? translateText(translate, "summary_v3_normal_by_ignored", "Normal por ignorado") : getCageShortTag(cage, translate),
      ],
      ...(ignored ? [] : sensorRows),
    ];
  }, [canSeeSensorFailures, cage, ignored, translate]);
  const tone = useMemo(() => (ignored ? "normal" : getSimpleCageTone(cage)), [cage, ignored]);

  const clearCloseTimer = useCallback(() => {
    if (!closeTimerRef.current) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleOtherTooltipOpen = (event) => {
      if (event.detail?.selectionKey === cage.selectionKey) return;
      clearCloseTimer();
      tooltipPinnedRef.current = false;
      setTooltipPinned(false);
      setTooltipPosition(null);
    };

    window.addEventListener(SUMMARY_V3_MAP_TOOLTIP_EVENT, handleOtherTooltipOpen);

    return () => {
      window.removeEventListener(SUMMARY_V3_MAP_TOOLTIP_EVENT, handleOtherTooltipOpen);
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, [cage.selectionKey, clearCloseTimer]);

  const showTooltip = useCallback((pin = false) => {
    clearCloseTimer();
    const rect = hostRef.current?.getBoundingClientRect();
    if (!rect) return;

    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(SUMMARY_V3_MAP_TOOLTIP_EVENT, {
        detail: { selectionKey: cage.selectionKey },
      }));
    }

    if (pin) {
      tooltipPinnedRef.current = true;
      setTooltipPinned(true);
    }

    const tooltipHeight = canSeeSensorFailures ? 320 : 180;
    const spacing = 8;
    const opensDown = rect.bottom + tooltipHeight + spacing <= window.innerHeight;
    const nextTop = opensDown
      ? rect.bottom + spacing
      : rect.top - tooltipHeight - spacing;

    setTooltipPosition({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 420)),
      top: Math.max(8, Math.min(nextTop, window.innerHeight - tooltipHeight - spacing)),
    });
  }, [canSeeSensorFailures, cage.selectionKey, clearCloseTimer]);

  const hideTooltip = useCallback(() => {
    clearCloseTimer();
    tooltipPinnedRef.current = false;
    setTooltipPinned(false);
    setTooltipPosition(null);
  }, [clearCloseTimer]);

  const scheduleHideTooltip = useCallback(() => {
    clearCloseTimer();
    if (tooltipPinnedRef.current || tooltipPinned) return;
    closeTimerRef.current = window.setTimeout(() => {
      setTooltipPosition(null);
    }, 180);
  }, [clearCloseTimer, tooltipPinned]);
  const handleShowTooltip = useCallback(() => showTooltip(false), [showTooltip]);
  const handleClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    showTooltip(true);
  }, [showTooltip]);

  return (
    <span
      ref={hostRef}
      className="summary-v3__mapCageHost"
      tabIndex={0}
      onMouseEnter={handleShowTooltip}
      onMouseLeave={scheduleHideTooltip}
      onFocus={handleShowTooltip}
      onBlur={scheduleHideTooltip}
      onClick={handleClick}
    >
      <span className={`summary-v3__cageDot summary-v3__cageDot--${tone}`}>
        {cage.alias}
      </span>
      {tooltipPosition && typeof document !== "undefined"
        ? createPortal(
            <span
              className="summary-v3__tooltipBox summary-v3__tooltipBox--floating summary-v3__tooltipBox--mapCage"
              role="tooltip"
              style={{
                left: tooltipPosition.left,
                top: tooltipPosition.top,
              }}
              onMouseEnter={clearCloseTimer}
              onMouseLeave={hideTooltip}
            >
              <strong>{centerName} / {translateText(translate, "jaula", "Jaula")} {cage.alias}</strong>
              <span className="summary-v3__tooltipRows">
                {rows.map(([label, detail], index) => (
                  <span key={`${label}-${detail}-${index}`} className="summary-v3__tooltipRow">
                    <span>{label}</span>
                    <em>{detail}</em>
                  </span>
                ))}
              </span>
              {canSeeSensorFailures && !ignored ? <SummaryV3AlarmTrendChart points={alarmTrend} translate={translate} /> : null}
            </span>,
            document.body
          )
        : null}
    </span>
  );
});
